import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { AD_OPT_OUT_INTEGRATIONS, CONSENT_COOKIE_EXPIRY_DAYS, CONSENT_COOKIE_NAME } from '@ecom/common';
import { SEGMENT_CONSENT_EVENT } from '../analytics/analytics.constants';
import { EnvService } from '../env/env.service';
import type { ScriptLoaderInlineScript, ScriptLoaderScript } from './script-loader-interfaces';
import { ServerScriptLoaderService } from './server-script-loader.service';

/**
 * The ScriptLoaderService allows for dynamic insertion of marketing scripts on the server or the browser.
 * This is accomplished by using the injectScriptByType function and by passing in a script of type ScriptLoaderScriptType.
 *
 * @example
 *   service.injectScriptByType('gtm');
 */

@Injectable({
  providedIn: 'root',
})
export class ScriptLoaderService extends ServerScriptLoaderService {
  constructor(@Inject(DOCUMENT) public doc: Document, public env: EnvService) {
    super(doc, env);
  }

  public returnScriptByType(type: string, idOverride?: string): ScriptLoaderScript | ScriptLoaderInlineScript {
    switch (type) {
      case 'segment': {
        const writeKey = idOverride ? idOverride : this.env.get.segmentId;
        const apiProxy = this.env.get.segmentApiProxy;
        const proxyIntegrations = apiProxy.length > 0 ? { 'Segment.io': { apiHost: `${apiProxy}/v1` } } : {};
        return {
          // TODO: Replace segmentId.driving with segmentId.homepage after cutover. There are 2 instances to be replaced in the text below.
          // Opt-out (consent cookie other than '0', or Global Privacy Control) is resolved and a GPC
          // opt-out persisted BEFORE analytics.load(), so ad destinations are switched off at load and
          // on the first page() — the same rule as @ecom/common isAdOptOut.
          text: `
          !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="${
            this.env.get.segmentCdnProxy.length > 0
              ? `https://${this.env.get.segmentCdnProxy}/analytics.js/v1/`
              : `https://cdn.segment.com/analytics.js/v1/`
          }" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics._writeKey="${writeKey}";;analytics.SNIPPET_VERSION="4.15.3";

          var consentCookie = XGRITgetCookie('${CONSENT_COOKIE_NAME}');
          var hasGpc = XGRIThasGpc();
          var gpcOptOutApplied = hasGpc && consentCookie !== '1';
          if (gpcOptOutApplied) {
            var host = window.location.hostname;
            var domainAttr = host.indexOf('.') > -1 && !/^\\d+(\\.\\d+){3}$/.test(host) ? '; domain=' + host.split('.').slice(-2).join('.') : '';
            try {
              document.cookie = '${CONSENT_COOKIE_NAME}=1; expires=' + new Date(Date.now() + ${CONSENT_COOKIE_EXPIRY_DAYS} * 864e5).toUTCString() + '; path=/' + domainAttr;
            } catch (e) {
              // Cookies blocked: the opt-out still applies to this page view.
            }
            consentCookie = '1';
          }
          var optedOut = hasGpc || (consentCookie !== null && consentCookie !== '0');
          var adOptOutIntegrations = ${JSON.stringify(AD_OPT_OUT_INTEGRATIONS)};
          var loadIntegrations = Object.assign({}, optedOut ? adOptOutIntegrations : {}, ${JSON.stringify(
            proxyIntegrations,
          )});

          // Re-check the opt-out on every event (Signals, third-party calls, mid-session opt-outs).
          analytics.addSourceMiddleware(function (m) {
            if (XGRITisOptedOut()) {
              m.payload.obj.integrations = Object.assign({}, m.payload.obj.integrations, adOptOutIntegrations);
              m.payload.obj.context = Object.assign({}, m.payload.obj.context, { consent: { categoryPreferences: { DataSharing: false } } });
            }
            m.next(m.payload);
          });

          analytics.load("${writeKey}", Object.keys(loadIntegrations).length ? { integrations: loadIntegrations } : undefined);

          var eventOptions = { consent: { categoryPreferences: { DataSharing: !optedOut } } };
          if (optedOut) {
            eventOptions.integrations = adOptOutIntegrations;
          }

          analytics.page({
              url: location.href,
              path: location.pathname,
            }, eventOptions);

          if (gpcOptOutApplied) {
            analytics.track('${SEGMENT_CONSENT_EVENT}', {}, eventOptions);
          }
          }}();

    function XGRITgetCookie(name) {
      try {
        const match = document.cookie.match(new RegExp('(?:^|;\\\\s*)' + name + '=([^;]*)'));
        return match ? match[1] : null;
      } catch (e) {
        return null;
      }
    }

    // A throwing getter reads as no signal, matching @ecom/common hasGlobalPrivacyControl.
    function XGRIThasGpc() {
      try {
        return !!(window.navigator && window.navigator.globalPrivacyControl);
      } catch (e) {
        return false;
      }
    }

    function XGRITisOptedOut() {
      var cookie = XGRITgetCookie('${CONSENT_COOKIE_NAME}');
      return XGRIThasGpc() || (cookie !== null && cookie !== '0');
    }
          `,
          type: 'segment',
          location: 'head',
        };
      }
      case 'optimizely':
        return {
          src: `https://cdn.optimizely.com/js/${idOverride ? idOverride : this.env.get.optimizelyId}.js`,
          type: 'optimizely',
          location: 'head',
        };
      case 'trustpilot':
        return {
          src: 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js',
          type: 'trustpilot',
          location: 'head',
          attrs: ['async'],
        };
      case 'hubspot':
        return {
          src: 'https://js.hsforms.net/forms/v2.js',
          type: 'hubspot',
          location: 'head',
        };
      case 'feefo':
        return {
          src: `https://api.feefo.com/api/javascript/${this.env.get.feefoMarchantId}`,
          type: 'feefo',
          location: 'head',
          attrs: ['async'],
        };
      default:
        return undefined;
    }
  }
}
